import { FormInput, FormInputColor, FormSelect, FormState, FormTextArea, Labeled } from 'component_utils/FormUtils'
import { itemFilter } from 'component_utils/utils'
import ProcessingButton from 'components/ProcessingButton'
import SimpleModal from 'components/SimpleModal'
import { ISimpleUserDetails, IUserTask } from 'interfaces'
import React, { FC, useState } from 'react'
import { FaShareAlt, FaTimes } from 'react-icons/fa'
import { Badge, Button, FormGroup, Input, Label } from 'reactstrap'
import { useApi } from 'utils/API'
import Translation from 'utils/Language/Translation'
import { GiStrikingArrows } from 'react-icons/gi'
import { useTranslations } from 'utils/language'
import { useSpecializations } from 'utils/SpecializationProvider'

const TaskInitializer: FC<{
  trigger: any, 
  users: ISimpleUserDetails[],  
  reloadTasks: any,
}> = ({
  trigger,
  users,
  reloadTasks,
}) => {
  const [state, setState] = useState<any>({})
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const specializations = useSpecializations()
  const api = useApi()
  const T = useTranslations()

  const toUserTasks = (shared: boolean): IUserTask[] => {
    const base: IUserTask = {
      id: null,
      createdAt: null,
      updatedAt: null,
      finishedAt: null,
      startAt: null,
      version: 0,
      claimedByUser: null,
      timesWorked: [],
      priority: state.priority,
      tags: state.tags,
      users: selectedUsers.map(it => parseInt(it)),
      userFields: state.userFields,
      borderColor: state.borderColor,
      title: state.title,
      text: state.text,
    }

    if (shared) {
      return [base]
    } else {
      return base.users.map(it => ({
        ...base,
        users: [it]
      }))
    }
  }

  const customFields = specializations?.augmentations?.userTasks?.taskEditor?.(state, setState)

  return (
    <SimpleModal
      title={<Translation name="T.objects.usertasks.createNewTask"/>}
      fullscreen
      trigger={open => trigger((template: any) => {
        setState({
          title: template.title,
          text: template.text,
          borderColor: template.borderColor,
          priority: template.priority,
          tags: [],
          userFields: {}
        })
        setSelectedUsers(template.users.map((it: any) => ''+it))
        open()
      })}
      buttons={close => [
        <ProcessingButton color='info' onClick={async () => {
          await Promise.all(toUserTasks(false).map(t => api.userTasks.saveUserTask(t)))
          reloadTasks()
          close()
        }}>
          <GiStrikingArrows/> <Translation name="T.objects.usertasks.createPerUserTask" />
        </ProcessingButton>,
        <ProcessingButton color='primary' onClick={async () => {
          await Promise.all(toUserTasks(true).map(t => api.userTasks.saveUserTask(t)))
          reloadTasks()
          close()
        }}>
          <FaShareAlt/> <Translation name="T.objects.usertasks.createSharedTask" />
        </ProcessingButton>,
      ]}
    >
      <FormState state={state} setState={setState} disableFormWrapper>
          <Labeled title={<Translation name="T.misc.title"/>}>
            <FormInput path='title'/>
          </Labeled>
          <Labeled title={<Translation name="T.misc.text"/>}>
            <FormTextArea path='text' rows={5}/>
          </Labeled>
          <Labeled title={<Translation name="T.misc.borderColor"/>}>
            <FormInputColor path='borderColor'/>
          </Labeled>

        <Labeled title={<Translation name="T.misc.priority"/>}>
          <FormSelect
            path='priority'
            options={[
              { value:  0, label: T("T.misc.priorities.very-low") },
              { value: 10, label: T("T.misc.priorities.low") },
              { value: 20, label: T("T.misc.priorities.normal") },
              { value: 30, label: T("T.misc.priorities.high") },
              { value: 40, label: T("T.misc.priorities.very-high") },
            ]}
          />
        </Labeled>

        {customFields ? customFields : null}

        <FormGroup>
          <Label for='user-selector'>User selection</Label>
          <Input id='user-selector' type='select' onChange={e => {
            const value = e.target.value
            setSelectedUsers(old => [...old, value])
            e.target.value = ''
          }}>
            <option value=""></option>
            {users.filter(it => !selectedUsers.includes(''+it.userid)).map(user => {
              return (<option key={user.userid} value={user.userid}>{user.username}</option>)
            })}
          </Input>
        </FormGroup>

        <div className='w-100 mb-5'>
          {selectedUsers.map(userid => {
            const user = users.find(it => (''+it.userid) === userid)
            return (
              <Badge key={userid} pill className='mr-1'>
                {user.username}
                <Button className='p-0' onClick={() => setSelectedUsers(old => old.filter(itemFilter([userid])))}>
                  <FaTimes/>
                </Button>
              </Badge>
            )
          })}
        </div>
      </FormState>
    </SimpleModal>
  )
}

export default TaskInitializer